import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Inscription,
  Preloader,
  RequiredIcon,
  SelectDropdown,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';
import { Option } from '@common-fe/common-fe';

import { useAuthStore } from '@/modules/user/stores';
import { getFullAddress } from '@/utils';

import { PaymentMethodLabel } from '../../ReimburseMe/ConfirmationStep/Immediately/PaymentMethodLabel';
import { usePaymentMethodHasPendingMessage } from '../../ReimburseMe/ConfirmationStep/Immediately/PaymentMethodLabel/usePaymentMethodHasPendingMessage';

import { PaymentMethodFields } from './Checks.styled';
import { PaymentMethod, PaymentMethodOwnerType } from './constants';
import { Check } from './constants';
import PaymentMethodOption from './PaymentMethodOption';
import { useGetPaymentMethods } from './useGetPaymentMethods.query';
import useReissueCheckQuery from './useReissueCheck.query';

interface Props {
  unavailablePaymentMethodId?: string;
  check: Check;
  setIsDisabledPaymentMethod?: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmittingReissueCheck?: boolean;
  setIsSubmittingReissueCheck?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsReissuedSuccessfully?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReissueCheck: React.FC<Props> = ({
  setIsReissuedSuccessfully,
  isSubmittingReissueCheck,
  setIsSubmittingReissueCheck,
  setIsDisabledPaymentMethod,
  check,
  unavailablePaymentMethodId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const { formattedData: paymentMethods, isLoading: areMethodsLoading } = useGetPaymentMethods(user?.employee?.id);
  const ownerType = useMemo(() => paymentMethods.find((method) => method.id === check.paymentMethodId)?.paymentMethodOwner?.ownerType, [paymentMethods, check]);
  const isProvider = useMemo(() => ownerType === PaymentMethodOwnerType.PROVIDER, [ownerType]);
  const filteredPaymentMethods = useMemo(() => paymentMethods
    .filter((method) => method.paymentMethodOwner?.ownerType === ownerType && method?.id !== unavailablePaymentMethodId),
    [paymentMethods, ownerType, unavailablePaymentMethodId]);
  const paymentMethodsOptions = useMemo<Option[]>(() => filteredPaymentMethods.map((method) => ({
    key: method.id,
    value: method.id,
    node: <PaymentMethodOption method={method} />,
  } as Option)), [filteredPaymentMethods]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  useEffect(() => {
    if (!filteredPaymentMethods?.length) return;

    if (!selectedPaymentMethodId) {
      const checkedPaymentMethod = filteredPaymentMethods.find((method) => method.id === check.paymentMethodId);
      setPaymentMethod(checkedPaymentMethod);
      setSelectedPaymentMethodId(checkedPaymentMethod?.id || '');
    }

    if (selectedPaymentMethodId) {
      setPaymentMethod(filteredPaymentMethods.find((method) => method.id === selectedPaymentMethodId));
    }
  }, [filteredPaymentMethods, setPaymentMethod, selectedPaymentMethodId, setSelectedPaymentMethodId, check]);
  const paymentMethodFullAddress = useMemo(() => getFullAddress(paymentMethod?.address), [paymentMethod]);
  const onSuccess = useCallback(() => {
    setIsReissuedSuccessfully?.(true);
    setIsSubmittingReissueCheck?.(false);
  }, [setIsReissuedSuccessfully, setIsSubmittingReissueCheck]);
  const { mutateAsync: reissueCheck, isLoading } = useReissueCheckQuery(onSuccess, check.id);
  useEffect(() => setSelectedPaymentMethodId(''), [setSelectedPaymentMethodId]);

  const { hasAccountVerificationPendingMessage, isLoading: isDataLoading } = usePaymentMethodHasPendingMessage(paymentMethod, true);
  useEffect(() => {
    setIsDisabledPaymentMethod?.(Boolean(hasAccountVerificationPendingMessage || isDataLoading || isLoading));
  }, [setIsDisabledPaymentMethod, hasAccountVerificationPendingMessage, isDataLoading, isLoading]);

  useEffect(() => {
    if (isSubmittingReissueCheck && selectedPaymentMethodId) {
      reissueCheck(selectedPaymentMethodId as string);
    }
  }, [isSubmittingReissueCheck, reissueCheck, selectedPaymentMethodId]);

  return (
    <Box data-testId="ReissueCheck">
      <PaymentMethodFields
        style={{ boxShadow: '0px 6px 15px 0px rgba(0, 0, 0, 0.03)' }}
        pad="spacing24"
        background="canvas"
        round="small"
        border={{ color: 'border2' }}
      >
        {isProvider ? null : (
          <Box
            direction="row"
            align="center"
            justify="between"
          >
            <Box width={{ min: '244px' }}>
              <Box direction="row" margin={{ bottom: 'spacing4' }}>
                <RequiredIcon color="danger" size="8px" style={{ marginRight: '2px', marginTop: '4px' }} />
                <Inscription weight="bold" lineHeight="20px" color="textTitle">{t('Payment method')}</Inscription>
              </Box>
              <Inscription color="textSecondary" weight={500} lineHeight="18px" size="small">
                {t('Select an alternative payment method previously created by the employee.')}
              </Inscription>
            </Box>
            <Box fill pad={{ left: 'spacing12' }}>
              {areMethodsLoading ? <Preloader /> : (
                <SelectDropdown
                  name="payment_method"
                  testId="payment_method"
                  id="payment_method"
                  options={paymentMethodsOptions}
                  singleMode
                  onChange={(value) => setSelectedPaymentMethodId(value as string)}
                  value={selectedPaymentMethodId}
                  placeholder={t('Select payment method')}
                />
              )}
            </Box>
          </Box>
        )}

        {paymentMethod ? (
          <Box>
            <Box direction="row">
              <Box width={{ min: '256px' }}>
                <Inscription
                  color="textBody"
                  weight="bold"
                  lineHeight="20px"
                >
                  {paymentMethodFullAddress ? 'Address' : 'Details'}
                </Inscription>
              </Box>
              <PaymentMethodLabel
                paymentMethod={paymentMethod}
                hasAccountVerificationPendingMessage={hasAccountVerificationPendingMessage}
              />
            </Box>
            <Box>
              {hasAccountVerificationPendingMessage ? (
                <Box margin={{ top: 'spacing12' }}>
                  <WarningCard
                    containerProps={{
                      pad: { vertical: 'spacing16', horizontal: 'spacing24' },
                    }}
                  >
                    <Box direction="row" align="center">
                      <WarningIcon />
                      <Inscription color="textBody" weight="bold" lineHeight="20px" margin={{ left: 'spacing8' }}>
                        You cannot use this payment method yet
                      </Inscription>
                    </Box>
                    <Inscription color="textBody" lineHeight="20px" margin={{ top: 'spacing8' }}>
                      This payment method is still pending verification. You'll be able to use it as soon as your bank account is confirmed.
                    </Inscription>
                  </WarningCard>
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </PaymentMethodFields>
    </Box>
  );
};
